import React from 'react'
import { t } from 'i18next'
import moment from 'moment'
import Badge from 'app/app/(client)/components/molecules/Badge/Badge'
import Checkbox from 'app/app/(client)/components/molecules/Checkbox/Checkbox'
import IconwithText from 'app/app/(client)/components/molecules/IconwithText/IconwithText'
import Tooltip from 'app/app/(client)/components/molecules/Tooltip/Tooltip'
import Card from 'app/app/(client)/components/atoms/Card/Card'
import Text from 'app/app/(client)/components/atoms/Text/Text'
import SalesforceSyncOpp from 'app/app/(client)/components/organisms/SalesforceSync/SalesforceSyncOpp'
import StatusColumn from 'app/app/(client)/components/templates/Opportunity/StatusColumn'
import { useAppDispatch, useAppSelector } from 'app/app/(client)/hooks/redux-hook'
import { addOpportunityId, removeOpportunityId } from '../../../redux/features/customers/opportunity-slice'
import { Icons } from 'app/app/(client)/utils/client-constants'
import { BuildingEquipment, OpportunityModel } from 'app/types/ui/opportunity'
import { onOpportuitySelect, validateInitialParams } from 'app/app/(client)/utils/opportunity-utils'
import { useNavigate } from 'app/app/(client)/hooks/useNavigate'

const Opportunity: React.FC<OpportunityModel> = ({
  id,
  customerId,
  name,
  dueDate,
  category,
  owner,
  offer,
  buildingEquipments = [{ element: Icons.location, count: 0 }] as BuildingEquipment[]
}) => {
  const dispatch = useAppDispatch()
  const router = useNavigate()
  const { showShares } = useAppSelector(state => state.opportunities)
  const { data: syncStatus } = useAppSelector(state => state.currentSync)
  const { data: customersData } = useAppSelector(state => state.customers)
  const { data: configData } = useAppSelector(state => state.configdata)
  const SFSyncStatus = id && syncStatus ? syncStatus[id] : undefined
  const opportunityHoverEffect =
    SFSyncStatus && SFSyncStatus.syncedWithSF ? 'md:hover:!border-[1px] md:hover:!border-primary' : ''
  const opportunityBackgroundColor =
    SFSyncStatus && !SFSyncStatus.syncedWithSF ? '!bg-content-switcher-background' : 'bg-white md:!bg-background-grey'
  const { selectedForUnShare } = useAppSelector(state => state.opportunities)
  const checkboxChecked: boolean = selectedForUnShare.includes(id)
  const { opportunityShares } = offer || {}

  const opportunitySharesSection =
    showShares && offer && opportunityShares && opportunityShares.length > 0
      ? opportunityShares.map(coworker => {
          const firstName = coworker.firstName || ''
          const lastName = coworker.lastName || ''
          const firstNameInitial = firstName.length > 0 ? firstName[0].toUpperCase() : '?'
          const lastNameInitial = lastName.length > 0 ? lastName[0].toUpperCase() : '?'

          return (
            <Tooltip
              key={coworker.userId}
              id={coworker.userId}
              content={`${firstName} ${lastName}`}
              position="top"
              className="inline-block"
            >
              <div key={coworker.userId} className="profile-circle">
                <div className="w-[22px] h-[20px]">
                  {firstNameInitial}
                  {lastNameInitial}
                </div>
              </div>
            </Tooltip>
          )
        })
      : null

  const handleClick = React.useCallback(() => {
    if (SFSyncStatus?.syncedWithSF === false) {
      return
    }

    // Early check - ensure data is loaded
    if (!customersData?.customers || !customersData?.offers || !configData?.frontLineConfig || !configData?.profile) {
      console.warn('Data not yet loaded for opportunity selection')
      return
    }

    const customer = customersData.customers.find(cust => cust.id === customerId)

    if (!customer?.id) {
      console.error('Customer not found:', customerId)
      return
    }

    if (!validateInitialParams(id, offer?.id, customersData.customers, customersData.offers)) {
      console.error('Invalid opportunity parameters')
      router.push('/')
      return
    }
    onOpportuitySelect(configData.frontLineConfig, dispatch, id, configData.profile, customer.id, router)
  }, [SFSyncStatus, customersData, customerId, configData, dispatch, router, id, offer])

  const handleOpportunityCheckboxChange = (event: React.MouseEvent<HTMLDivElement>, isChecked: boolean) => {
    event.stopPropagation()
    if (isChecked) {
      dispatch(addOpportunityId(id))
    } else {
      dispatch(removeOpportunityId(id))
    }
  }

  return (
    <Card
      id={`opportunity-card-${id}`}
      className={`px-4 py-[18px] md:p-6 border-[0.5px] border-border-card cursor-pointer shadow-small md:shadow-none md:rounded-lg md:border-transparent ${opportunityBackgroundColor} ${opportunityHoverEffect}`}
    >
      <div onClick={handleClick}>
        <div className="flex justify-between ">
          <div className="grid grid-cols-12 w-full gap-x-6">
            <div className="col-span-12 md:col-span-6">
              <div className="flex gap-x-6">
                {!showShares && (
                  <Checkbox
                    id={`opportunity-checkbox-${id}`}
                    checked={checkboxChecked}
                    onChecked={(event, checked) => handleOpportunityCheckboxChange(event, checked)}
                  />
                )}
                <Text
                  id={`opportunity-name-${id}`}
                  className="font-bold text-black text-16px leading-6 md:text-grey-icon md:text-14px md:leading-5"
                >
                  {name}
                </Text>
              </div>
            </div>
            {showShares ? (
              <div className="col-span-6 text-[14px] gap-6 hidden md:flex items-center">
                {opportunitySharesSection ? (
                  <>
                    {t('customers.refresh.sharedBy')}: {opportunitySharesSection}
                  </>
                ) : null}
              </div>
            ) : (
              <Text
                id={`opportunity-owner-${id}`}
                as="div"
                className="hidden md:block text-14px font-normal col-span-6"
              >
                {t('customers.refresh.owner')}: {owner}
              </Text>
            )}
          </div>
          <SalesforceSyncOpp opportunityId={id} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 lg:grid-cols-12 items-center gap-y-[12px]  mt-[12px] md:mt-[24px]  md:gap-y-[0px]">
          <div className="col-span-2 md:col-span-2 lg:col-span-2 md:mr-6 ">
            <Badge
              id={`opportunity-category-${id}`}
              className="text-12px font-normal leading-[16px]"
              type="category"
              label={category}
            />
          </div>

          <div className="col-span-2 md:col-span-3 lg:col-span-4 flex flex-row md:flex-col lg:flex-row items-center flex-wrap gap-x-4">
            {buildingEquipments.map((eqGroup: BuildingEquipment, index) => (
              <IconwithText
                key={index.toString()}
                id={`opportunity-equipment-${id}-${index}`}
                iconName={`${eqGroup.element} text-16px`}
                className="gap-[6px] text-black"
                textContent={[
                  { text: eqGroup.count.toString(), description: t(eqGroup.elementDescription), style: 'text-14px ' }
                ]}
              />
            ))}
          </div>

          <div className="hidden md:block md:col-span-4 lg:col-span-4 ">
            <StatusColumn opportunityId={id} offer={offer} SFSyncStatus={SFSyncStatus} />
          </div>

          <Text
            id={`opportunity-closedate-${id}`}
            as="div"
            className="hidden md:block md:col-span-3 lg:col-span-2 font-normal text-left md:text-right text-14px"
          >
            {t('customers.refresh.closeDate')}: {moment(dueDate).format('DD MMM YYYY')}
          </Text>

          <Text
            id={`opportunity-closedate-${id}`}
            as="div"
            className="block md:hidden col-span-1 font-bold text-left text-grey-icon text-14px"
          >
            {t('customers.refresh.closeDate')}:
          </Text>

          <Text
            id={`opportunity-closedate-${id}`}
            as="div"
            className="block md:hidden col-span-1 font-normal text-right text-14px"
          >
            {moment(dueDate).format('DD MMM YYYY')}
          </Text>

          {showShares ? (
            <div className="col-span-2 text-[14px] gap-[24px] flex items-center md:hidden">
              {opportunitySharesSection ? (
                <>
                  {t('customers.refresh.sharedBy')}: {opportunitySharesSection}
                </>
              ) : null}
            </div>
          ) : (
            <>
              <Text
                id={`opportunity-mobile-owner-text-${id}`}
                as="div"
                className="block md:hidden text-14px font-normal text-left col-span-1"
              >
                {t('customers.refresh.owner')}:
              </Text>
              <Text
                id={`opportunity-mobile-owner-name-${id}`}
                as="div"
                className="block md:hidden text-14px font-normal text-right col-span-1"
              >
                {owner}
              </Text>
            </>
          )}

          <div className="col-span-2 md:hidden w-full">
            <StatusColumn opportunityId={id} offer={offer} SFSyncStatus={SFSyncStatus} />
          </div>
        </div>
      </div>
    </Card>
  )
}

export default Opportunity
